import { t as tr } from '../i18n.js'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { api, type HomeDashboard } from '../api.js'
import { useAuth } from '../auth.js'
import { usePref } from '../prefs.js'
import { fmtDay, fmtMinutes } from '../time.js'
import { Stat, WeeklyBars } from './charts.js'

export function HomePage() {
  const { user } = useAuth()
  const q = useQuery({ queryKey: ['dashboard', 'me'], queryFn: () => api<HomeDashboard>('/dashboard/me'), refetchInterval: 60_000 })
  const [hidden] = usePref<string[]>('hiddenProjects', [])
  if (!q.data) return <p className="muted">{tr("Loading…")}</p>
  const d = { ...q.data, projects: q.data.projects.filter((p) => !hidden.includes(p.id)) }
  const delta = d.week.minutes - d.week.previousMinutes
  const overdue = d.myTasks.filter((t) => t.overdue).length
  return (
    <>
      <h1>Welcome, {user?.displayName}</h1>
      <div className="stats">
        <Stat n={fmtMinutes(d.week.minutes)} label={tr("logged this week")} sub={`${delta >= 0 ? '+' : '−'}${fmtMinutes(Math.abs(delta))} vs last week`} />
        <Stat n={d.myTasks.length} label={tr("open tasks assigned to me")} sub={overdue ? `${overdue} overdue` : undefined} bad={overdue > 0} />
        <Stat n={d.projects.length} label={tr("projects")} sub={`${d.projects.filter((p) => p.myRole).length} as a member`} />
        <Stat n={d.outstanding.length} label={tr("weeks awaiting submission")} sub={tr("approvals arrive in Phase 3")} />
      </div>

      <div className="grid2">
        <section className="card stack">
          <h2>This week · {fmtDay(d.week.from)} – {fmtDay(d.week.to)}</h2>
          {d.week.byProject.length === 0 && <p className="muted small">{tr("Nothing logged yet.")} <Link to="/time">{tr("Log time →")}</Link></p>}
          {d.week.byProject.map((p) => (
            <div key={p.projectId} className="row between"><span><strong>{p.code}</strong> <span className="muted">{p.name}</span></span><span className="mono">{fmtMinutes(p.minutes)}</span></div>
          ))}
          {d.insufficientHistory
            ? <p className="muted small">{tr("Not enough history yet for a trend — the weekly chart appears after two weeks of entries.")}</p>
            : <WeeklyBars points={d.weekly} weeks={12} title="Hours per week (your calendar)" />}
        </section>

        <section className="card stack">
          <h2>{tr("My tasks")}</h2>
          {d.myTasks.length === 0 && <p className="muted small">{tr("Nothing assigned to you.")}</p>}
          {d.myTasks.slice(0, 12).map((t) => (
            <div key={t.id} className="row between small">
              <span><Link to={`/projects/${t.projectId}?task=${t.id}`}><span className="mono muted">{t.key}</span> {t.title}</Link>{t.priority && t.priority !== 'medium' && <span className={`tag prio-${t.priority}`}>{t.priority}</span>}</span>
              <span className={t.overdue ? 'bad' : 'muted'}>{t.dueOn ?? t.statusName}</span>
            </div>
          ))}
          {d.myTasks.length > 12 && <p className="muted small">…and {d.myTasks.length - 12} more</p>}
        </section>
      </div>

      <section className="card stack">
        <h2>{tr("Projects")}</h2>
        <table className="table">
          <thead><tr><th>{tr("Project")}</th><th>{tr("Progress")}</th><th className="num">{tr("Mine open")}</th><th className="num">{tr("Hours (30d)")}</th><th></th></tr></thead>
          <tbody>
            {d.projects.map((p) => (
              <tr key={p.id}>
                <td><Link to={`/projects/${p.id}`}><strong>{p.code}</strong></Link> <span className="muted">{p.name}</span></td>
                <td style={{ minWidth: '10rem' }}><Progress done={p.tasksDone} total={p.tasksTotal} /></td>
                <td className="num mono">{p.openMine || ''}</td>
                <td className="num mono">{p.minutes30d ? fmtMinutes(p.minutes30d) : ''}</td>
                <td className="small"><Link to={`/projects/${p.id}/board`} data-tip={tr("Open the Kanban board")}>{tr("Board")}</Link> · <Link to={`/projects/${p.id}/gantt`} data-tip={tr("Open the Gantt chart")}>{tr("Gantt")}</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {d.outstanding.length > 0 && (
        <section className="card stack">
          <h2>{tr("Weeks awaiting submission")}</h2>
          <ul className="help small muted">
            <li>{tr("Time is approved per project, per week, in each project's own timezone.")}</li>
            <li>This list shows the project-weeks with time you have not submitted yet; submit them from <Link to="/timesheets">{tr("Timesheets")}</Link>.</li>
          </ul>
          <table className="table">
            <thead><tr><th>{tr("Project")}</th><th>{tr("Week of")}</th><th className="num">{tr("Hours")}</th><th>{tr("State")}</th></tr></thead>
            <tbody>
              {d.outstanding.map((o) => (
                <tr key={`${o.projectId}-${o.weekStart}`}><td><strong>{o.code}</strong> <span className="muted small">{o.timezoneOfTruth}</span></td><td>{o.weekStart}</td><td className="num mono">{fmtMinutes(o.minutes)}</td><td className="small">{o.state === 'rejected' ? <span className="bad">{tr("rejected — resubmit")}</span> : 'not yet submitted'}</td></tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  )
}

export function Progress({ done, total }: { done: number; total: number }) {
  const pct = total ? Math.round((done / total) * 100) : 0
  return (
    <div className="progress" title={`${done} of ${total} tasks done`}>
      <div className="bar" style={{ width: `${pct}%` }} />
      <span className="small mono">{total ? `${done}/${total} · ${pct}%` : 'no tasks'}</span>
    </div>
  )
}
